import React, { useState, useEffect } from 'react';
import { MessageSquare, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NotesTab = ({ entityType, entityId, currentUser }) => {
  const [notes, setNotes] = useState([]);
  const [newNoteBody, setNewNoteBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editBody, setEditBody] = useState('');

  useEffect(() => {
    if (entityId) {
      fetchNotes();
    }
  }, [entityId, entityType]);

  const fetchNotes = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/notes/${entityType}/${entityId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteBody.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          body: newNoteBody
        })
      });

      if (response.ok) {
        toast.success('Note added');
        setNewNoteBody('');
        await fetchNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create note');
      }
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Failed to create note');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editBody.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ body: editBody })
      });

      if (response.ok) {
        toast.success('Note updated');
        setEditingNoteId(null);
        setEditBody('');
        await fetchNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update note');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        toast.success('Note deleted');
        await fetchNotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="notes-tab">
      {/* Add Note Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Add Note</h3>
        <Textarea
          placeholder="Write your note here..."
          value={newNoteBody}
          onChange={(e) => setNewNoteBody(e.target.value)}
          rows={4}
          className="w-full"
          data-testid="new-note-textarea"
        />
        <Button
          onClick={handleCreateNote}
          disabled={loading || !newNoteBody.trim()}
          className="w-full sm:w-auto"
          data-testid="save-note-button"
        >
          {loading ? 'Saving...' : 'Save Note'}
        </Button>
      </div>

      {/* Notes List */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">
          Notes ({notes.length})
        </h3>
        
        {notes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-notes-state">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No notes yet</p>
            <p className="text-sm">Add your first note above</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="border border-border rounded-lg p-4 bg-card hover:bg-muted/50 transition-colors"
                data-testid={`note-${note.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground">
                        {note.author_name}
                      </span>
                      {note.is_edited && (
                        <span className="text-xs text-muted-foreground italic">
                          (Edited)
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(note.created_at)}
                    </span>
                  </div>
                  
                  {currentUser?.id === note.author_user_id && (
                    <div className="flex gap-2">
                      {editingNoteId === note.id ? (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateNote(note.id)}
                            data-testid="save-edit-button"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditBody('');
                            }}
                            data-testid="cancel-edit-button"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditBody(note.body);
                            }}
                            data-testid="edit-note-button"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid="delete-note-button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {editingNoteId === note.id ? (
                  <Textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    className="mt-2"
                    data-testid="edit-note-textarea"
                  />
                ) : (
                  <p className="text-sm text-foreground/80 whitespace-pre-wrap">
                    {note.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesTab;
